/** `/admin/tinjauan` — the "Didampingi" review queue (Phase 3, docs/nontech/CONTRACT.md). */
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'
import { AdminShell, adminPage } from '#/components/admin/admin-shell'
import { StatTile } from '#/components/admin/ui'
import { StatusBadge, packageName, rupiah } from '#/components/guided/guided-shared'
import { Button } from '#/components/ui/button'
import { Label } from '#/components/ui/label'
import { Textarea } from '#/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '#/components/ui/select'
import { ApiError } from '#/lib/api'
import { useClaimReview, useFinishReview, useReviewQueue, type ReviewRequestDto, type ReviewStatus } from '#/lib/api-guided'
import { fmtDateTime, fmtNumber } from '#/lib/format'
import { m } from '#/paraglide/messages'

const STATUSES: ReviewStatus[] = ['pending', 'paid', 'in_progress', 'done', 'cancelled']

export const Route = createFileRoute('/admin/tinjauan')({ component: adminPage(Page) })

function Page() {
  const [status, setStatus] = useState<'all' | ReviewStatus>('all')
  const { data, isLoading } = useReviewQueue(status === 'all' ? undefined : status)
  return (
    <AdminShell title={m.gd_admin_title()} description={m.gd_admin_desc()}>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile label={m.gd_admin_stat_open()} value={data ? fmtNumber(data.stats.open) : '…'} />
        <StatTile label={m.gd_admin_stat_overdue()} value={data ? fmtNumber(data.stats.overdue) : '…'} />
        <StatTile label={m.gd_admin_stat_total()} value={data ? fmtNumber(data.stats.total) : '…'} />
        <StatTile label={m.gd_admin_stat_revenue()} value={data ? rupiah(data.stats.revenueIdr) : '…'} />
      </div>

      <div className="w-48">
        <Select value={status} onValueChange={(v) => setStatus(v as 'all' | ReviewStatus)}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{m.gd_admin_filter_all()}</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                <StatusBadge status={s} />
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">{m.common_loading()}</p>
      ) : !data?.items.length ? (
        <p className="rounded-2xl border bg-card p-6 text-sm text-muted-foreground">{m.gd_admin_empty()}</p>
      ) : (
        <ul className="space-y-3" data-testid="review-queue">
          {data.items.map((r) => (
            <QueueRow key={r.id} r={r} />
          ))}
        </ul>
      )}
    </AdminShell>
  )
}

function QueueRow({ r }: { r: ReviewRequestDto }) {
  const claim = useClaimReview()
  const finish = useFinishReview()
  const [result, setResult] = useState('')
  const [open, setOpen] = useState(false)

  const doClaim = async () => {
    try {
      await claim.mutateAsync(r.id)
      toast.success(m.gd_admin_claimed())
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : m.common_error_generic())
    }
  }
  const doFinish = async () => {
    try {
      await finish.mutateAsync({ id: r.id, resultPlain: result })
      setOpen(false)
      setResult('')
      toast.success(m.gd_admin_finished())
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : m.common_error_generic())
    }
  }
  const actionable = r.status === 'paid' || r.status === 'in_progress'

  return (
    <li className="space-y-3 rounded-2xl border bg-card p-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{packageName(r.kind)}</span>
        <StatusBadge status={r.status} />
        <span className="text-sm text-muted-foreground">{rupiah(r.priceIdr)}</span>
        <span className="ml-auto text-xs text-muted-foreground">{fmtDateTime(r.createdAt)}</span>
      </div>
      <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
        <div className="flex gap-2">
          <dt className="text-muted-foreground">{m.gd_col_project()}</dt>
          <dd className="truncate">{r.planTitle ?? '—'}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="text-muted-foreground">{m.gd_col_customer()}</dt>
          <dd className="truncate">{r.userEmail ?? r.userId}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="text-muted-foreground">{m.gd_col_sla()}</dt>
          <dd className={r.slaOverdue ? 'text-status-failed' : undefined}>{r.slaAt ? (r.slaOverdue ? m.gd_sla_overdue() : fmtDateTime(r.slaAt)) : '—'}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="text-muted-foreground">{m.gd_admin_reviewer()}</dt>
          <dd className="truncate">{r.reviewerName ?? r.reviewerId ?? '—'}</dd>
        </div>
      </dl>
      {r.note ? <p className="rounded-lg bg-muted/50 p-2.5 text-sm whitespace-pre-wrap">{r.note}</p> : null}
      {r.resultPlain ? (
        <div className="rounded-lg border border-status-done/40 bg-status-done/5 p-3">
          <p className="text-xs font-medium tracking-wide text-status-done uppercase">{m.gd_result_title()}</p>
          <p className="mt-1 text-sm whitespace-pre-wrap">{r.resultPlain}</p>
        </div>
      ) : null}
      {actionable ? (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {r.status === 'paid' ? (
              <Button size="sm" variant="outline" disabled={claim.isPending} onClick={doClaim}>
                {m.gd_admin_claim()}
              </Button>
            ) : null}
            <Button size="sm" variant={open ? 'secondary' : 'default'} onClick={() => setOpen((v) => !v)}>
              {m.gd_admin_done()}
            </Button>
          </div>
          {open ? (
            <div className="space-y-2">
              <Label>{m.gd_admin_result_label()}</Label>
              <Textarea rows={3} value={result} onChange={(e) => setResult(e.target.value)} placeholder={m.gd_admin_result_ph()} />
              <Button size="sm" disabled={result.trim().length < 10 || finish.isPending} onClick={doFinish}>
                {m.gd_admin_done()}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  )
}
